import { Breadcrumb, Button, Card, Checkbox, Divider, Form, Input, Layout, message, Tooltip, Typography } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useForm } from "antd/es/form/Form";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo, useState } from "react";
import { FloatingWhatsApp } from "react-floating-whatsapp";
import { useSearchParams } from "react-router-dom";
import supportAvatar from '../assets/support.jpg';
import { AddressEditor } from "../components/AddressEditor";
import { IntlAddressEditor } from "../components/IntlAddressEditor";
import { GlobalStore } from "../store/store";
import { formatPrice, getPrice, getProduct } from "../utils";

interface IGoFarmCaptureProps {
  store: GlobalStore,
};

const { Content } = Layout;

export const GoFarmCapture: FC<IGoFarmCaptureProps> = observer(({ store }) => {
  const [form] = useForm();
  const [params] = useSearchParams();
  const plan = params.get("plano");
  const moeda = params.get("moeda");

  useEffect(() => {
    if (store.products.length === 0) {
      store.getProducts("gofarm");
    }
  }, []);

  const product = useMemo(() => {
    return getProduct(plan ?? "", store.products)?.[moeda ?? ""];
  }, [store.products, plan, moeda]);

  const onSubmit = async (values: any) => {
    if (!product) {
      message.warning("Erro ao carregar dados do produto. Recarregue a página ou entre em contato com o suporte Tecgraf.");
      return;
    }

    const body = {
      ExtProductID: product?.ProductID,
      ExtPriceID: product?.PriceID,
      Data: values,
    };

    const intent_resp = await fetch("/api/create-intent", {
      method: 'POST',
      body: JSON.stringify(body),
    });

    const intent = await intent_resp.json();

    const ps = new URLSearchParams();
    ps.set("product_id", product?.ProductID ?? '');
    ps.set("price_id", product?.PriceID ?? '');
    ps.set("intent_id", intent?.ID);

    const checkout_resp = await fetch("/api/create-checkout?" + ps.toString(), {
      method: 'POST',
    });

    const loc = await checkout_resp.json();
    if (loc?.URL) {
      window.location.href = loc.URL
    }
  }

  const { currency } = store;

  const products = useMemo(() => {
    return {
      anual: getProduct("anual", store.products),
      trimestral: getProduct("trimestral", store.products),
      // diario: getProduct("diario", store.products),
    };
  }, [store.products]);

  const discount = useMemo(() => {
    const p1 = products["anual"]?.[currency]?.UnitAmount;
    const p2 = products["trimestral"]?.[currency]?.UnitAmount;

    if (!p1 || !p2) {
      return 0;
    }

    return Math.round((1 - (p1 / (p2 * 4))) * 100 as any);
  }, [products, currency]);

  const price_anual = getPrice(products["anual"]?.[currency], currency)

  const [accepted_terms, set_accepted_terms] = useState(false);

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    form.setFieldValue("accepted_terms", e.target.checked);
    form.setFieldValue("accepted_terms_time", e.target.checked ? +(new Date()) : null);
    set_accepted_terms(e.target.checked);
  }

  return (
    <Layout>
      <Content style={{ padding: '0 50px 50px 50px', width: 1150, margin: "auto" }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <Breadcrumb.Item>Home</Breadcrumb.Item>
          <Breadcrumb.Item>{currency === "brl" ? "Produtos" : "Products"}</Breadcrumb.Item>
          <Breadcrumb.Item>GoFarm®</Breadcrumb.Item>
          <Breadcrumb.Item>{currency === "brl" ? "Registro" : "Register"}</Breadcrumb.Item>
        </Breadcrumb>

        <Card>
          <Typography.Title level={3} style={{ marginTop: 0 }}>
            GoFarm®
          </Typography.Title>

          <div>
            {currency === "brl" ? "Produto" : "Product"}: {product?.ProductName}
          </div>

          <div>
            {plan === "anual" &&
              <>
                {(discount >= 3) ?
                  <>
                    <p style={{ margin: 0, color: "#aaa" }}>
                      <span style={{ display: "inline-block", width: 44 }}>
                        {currency === "brl" ? "De:" : "From:"}
                      </span>
                      <span style={{ textDecoration: "line-through" }}>
                        {formatPrice(products["trimestral"]?.[currency]?.UnitAmount! * 4, currency)}
                      </span>
                    </p>
                    <p style={{ margin: 0 }}>
                      <span style={{ display: "inline-block", width: 44 }}>
                        {currency === "brl" ? "Por:" : "At:"}
                      </span>
                      <span>
                        {price_anual}
                      </span>
                    </p>
                  </>
                  :
                  <>
                    <p style={{ margin: 0 }}>
                      {price_anual}
                    </p>
                  </>}
              </>}

            {plan !== "anual" &&
              <>
                {currency === "brl" ? "Valor" : "Price"}: {getPrice(product, currency)}
              </>}
          </div>

          <Divider />

          <Form layout="vertical" form={form} onFinish={onSubmit}>

            <Typography.Title level={5}>
              {moeda === "brl" ? "Dados de Contato" : "Contact Details"}
            </Typography.Title>

            <Form.Item
              required
              rules={[{ required: true, message: moeda === "brl" ? 'Preencha o Nome Completo' : 'The Full Name is required' }]}
              name="nome_completo"
              label={moeda === "brl" ? "Nome Completo" : "Full Name"}
            >
              <Input />
            </Form.Item>

            <Form.Item
              required
              rules={[{ required: true, message: moeda === "brl" ? 'Preencha o Telefone' : 'The Phone is required' }]}
              name="telefone"
              label={moeda === "brl" ? "Telefone" : "Phone"}
            >
              <Input />
            </Form.Item>

            <Form.Item
              required
              rules={[{ required: true, message: moeda === "brl" ? 'Preencha o E-Mail' : 'The E-Mail is required' }]}
              name="email"
              label={moeda === "brl" ? "E-Mail" : "E-Mail"}
            >
              <Input />
            </Form.Item>

            <Typography.Title level={5}>
              {moeda === "brl" ? "Dados de Faturamento" : "Billing Details"}
            </Typography.Title>

            <Form.Item
              required
              rules={[{ required: true, message: moeda === "brl" ? 'Preencha o Nome / Razão Social' : 'The Name / Business Name is required' }]}
              name="razao_social"
              label={moeda === "brl" ? "Nome / Razão Social" : "Name / Business Name"}
            >
              <Input />
            </Form.Item>

            {moeda === "brl" &&
              <Form.Item label="Inscrição Estadual" name="inscricao_estadual">
                <Input />
              </Form.Item>}

            <Form.Item
              required
              rules={[{ required: true, message: moeda === "brl" ? 'Preencha o CPF / CNPJ' : 'The Tax ID is required' }]}
              name="tax_id"
              label={moeda === "brl" ? "CPF / CNPJ" : "Tax ID"}
            >
              <Input />
            </Form.Item>

            <div style={{ marginBottom: 10, fontWeight: "bold" }}>
              {moeda === "brl" ? "Endereço" : "Address"}
            </div>

            {moeda === "brl" ?
              <AddressEditor form={form} baseName="endereco" /> :
              <IntlAddressEditor form={form} baseName="endereco" />}

            <Form.Item
              required
              name="accepted_terms"
            >
              <Checkbox onChange={handleCheckboxChange}>
                {moeda === "brl" ?
                  <>
                    Li e concordo com o <a href={`${process.env.PUBLIC_URL}/doc/Contrato_pt-BR.pdf`} target="_blank">Contrato de Licença de Uso</a>
                  </>
                  :
                  <>
                    I have read and accept the <a href={`${process.env.PUBLIC_URL}/doc/Contrato_pt-BR.pdf`} target="_blank">Terms of Service (portuguese)</a>
                  </>}
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Tooltip
                title={accepted_terms ?
                  "" :
                  (moeda === "brl" ?
                    "É necessário aceitar o contrato de licença de uso para continuar." :
                    "It is necessary to accept the terms of service before continuing")}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!accepted_terms}
                  style={{ width: 140 }}
                >
                  {moeda === "brl" ? "Registrar" : "Register"}
                </Button>
              </Tooltip>
            </Form.Item>
          </Form>
        </Card>

        <FloatingWhatsApp
          accountName="Suporte Tecgraf Agro"
          phoneNumber="+5519999174690"
          avatar={supportAvatar}
          statusMessage="Responde em poucos minutos"
          chatMessage={`Bem-vindo ao suporte Tecgraf Agro! 🤝

Como podemos ajudar?`}
          placeholder="Digite sua mensagem"
        />

      </Content>
    </Layout>
  );
})